/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {inject, Injectable} from '@angular/core';
import {FabUser} from '@fabric/common/types';
import { OAuthService} from 'angular-oauth2-oidc';
import {BehaviorSubject} from 'rxjs';
import { Router } from '@angular/router';
import {APP_CONFIG} from '../../app.config';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {

  private _user: BehaviorSubject<FabUser> = new BehaviorSubject(null)
  private _userAuthorities: BehaviorSubject<String[]> = new BehaviorSubject([]);
  private _isInitialized = new BehaviorSubject<boolean>(false);
  public isInitialized = this._isInitialized.asObservable();
  private authConfig: any = {};

  constructor(private readonly oAuthService: OAuthService, private router: Router) {
    this.authConfig = inject(APP_CONFIG).auth;
  }

  async initialize(): Promise<boolean> {
    this.authConfig.redirectUri = window.location.origin;

    if (this.authConfig) {
      this.oAuthService.configure(this.authConfig);
      return this.oAuthService.loadDiscoveryDocumentAndLogin();
    } else {
      return false;
    }
  }

  /**
   * Acquires the access token of the logged-in user
   * @return string the logged-in user's access token appended with Bearer
   */
  getAuthorizationTokenHeader(): string {
    return `Bearer ${this.oAuthService.getAccessToken()}`;
  }

  enableSilentRenew(): void {
    if (this.oAuthService.getRefreshToken()) {
      this.oAuthService.setupAutomaticSilentRefresh({}, 'access_token', true);
    }
  }

  login(): void {
    this.oAuthService.initLoginFlow();
  }

  logout(): void {
    this.oAuthService.logOut();
  }

  isAuthenticated(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  /**
   * Creates a FabUser from the claims object passed in.
   *
   * @param claims Object consisting of the user's properties
   * @private
   */
  private constructUserFromIdentityClaims(claims: OAuthClaims): FabUser {
    let user: FabUser = {};
    if (claims) {
      user = {
        gmin: claims.gmuniqueidentifier ?? '',
        firstName: claims.givenName ?? '',
        lastName: claims.sn ?? '',
        displayName: claims.name ?? '',
        email: claims.email ?? '',
        roles: claims.roles ?? []
      };
    }
    return user;
  }

  get userInfo(): FabUser {
    const claims = this.oAuthService.getIdentityClaims() as OAuthClaims;
    return this.constructUserFromIdentityClaims(claims);
  }

  setUser(user: FabUser): void {
    this._user.next(user);

  }

  setAuthorities(authorities: String[]): void {
    this._userAuthorities.next(authorities);
  }

  get userAuthorities(): String[] {
    return this._userAuthorities.value;
  }

}



export interface OAuthClaims {
  aud: string,
  displayName: string,
  email: string,
  exp: number,
  givenName: string,
  gmDivision: string,
  gmuniqueidentifier: string,
  iat: number,
  iss: string,
  mail: string,
  name: string,
  nbf: number,
  nonce: string,
  oid: string,
  preferred_username: string,
  rh: string,
  sn: string,
  sub: string,
  tid: string,
  uid: string,
  uti: string,
  ver: string,
  roles?: string[]
}

